import React, {useEffect, useMemo, useRef} from 'react'
import {useSearchParams} from 'react-router-dom'
import classnames from 'classnames'
import {ListDealCard} from "./ListDealCard";
import {ListDealRow} from "./ListDealRow";
import {useMediaQuery} from "react-responsive";
import {mediaQueryForMobile} from "../HomePage/HomePage";
import CustomInfiniteScroll from "../../components/CustomInfiniteScroll";
import {useShallow} from "zustand/react/shallow";
import {useListDealsStore} from "../../stores/listDealsStore";
import {useDebounce} from "@uidotdev/usehooks";
import {useModalsStore} from "../../stores/modalsStore";

const LIST_COLUMNS = ['Vehicle', 'Spec', 'Lender', 'Type', 'Status', 'MSRP', 'Due / Down', 'Monthly', 'Term', 'Miles', 'Profit']

// Month break for the list view — full-width row. Rows stack vertically, so the
// arrows point up (toward the section above, `from`) and down (the section
// below, `to`) rather than sideways like the card-grid divider.
const MonthBreakRow = ({from, to}) => (
    <tr className={'lv-month-break'}>
        <td colSpan={LIST_COLUMNS.length}>
            <div className={'month-divider'}>
                <div className={'month-divider-col'}>
                    <span className={'m-name'}>{from}</span>
                    <span className={'m-arrow'}>
                        <svg viewBox="0 0 14 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <line x1="7" y1="36" x2="7" y2="0" stroke="currentColor" strokeWidth="1.5"/>
                            <polyline points="2,8 7,1 12,8" stroke="currentColor" strokeWidth="1.5"
                                      fill="none" strokeLinejoin="round" strokeLinecap="round"/>
                        </svg>
                    </span>
                </div>
                <div className={'month-divider-col'}>
                    <span className={'m-name'}>{to}</span>
                    <span className={'m-arrow'}>
                        <svg viewBox="0 0 14 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <line x1="7" y1="0" x2="7" y2="36" stroke="currentColor" strokeWidth="1.5"/>
                            <polyline points="2,28 7,35 12,28" stroke="currentColor" strokeWidth="1.5"
                                      fill="none" strokeLinejoin="round" strokeLinecap="round"/>
                        </svg>
                    </span>
                </div>
            </div>
        </td>
    </tr>
)

// Renders the "April ← → March" divider that appears between cards whose
// updated_at falls in different months. Two arrows point toward their own
// section so the eye reads "this is where April ends and March begins".
const MonthDivider = ({from, to}) => (
    <div className={'month-divider'}>
        <div className={'month-divider-col'}>
            <span className={'m-name'}>{from}</span>
            <span className={'m-arrow'}>
                <svg viewBox="0 0 36 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <line x1="36" y1="7" x2="0" y2="7" stroke="currentColor" strokeWidth="1.5"/>
                    <polyline points="8,2 1,7 8,12" stroke="currentColor" strokeWidth="1.5"
                              fill="none" strokeLinejoin="round" strokeLinecap="round"/>
                </svg>
            </span>
        </div>
        <div className={'month-divider-col'}>
            <span className={'m-name'}>{to}</span>
            <span className={'m-arrow'}>
                <svg viewBox="0 0 36 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <line x1="0" y1="7" x2="36" y2="7" stroke="currentColor" strokeWidth="1.5"/>
                    <polyline points="28,2 35,7 28,12" stroke="currentColor" strokeWidth="1.5"
                              fill="none" strokeLinejoin="round" strokeLinecap="round"/>
                </svg>
            </span>
        </div>
    </div>
)

const monthKey  = (d) => `${d.getFullYear()}-${d.getMonth()}`
const monthName = (d) => d.toLocaleString('en-US', {month: 'long'})

export const ListDealsList = () => {
    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    const findModalByName = useModalsStore((state) => state.findModalByName)

    const selectedDealId = findModalByName("DealModal")?.props?.id

    const {
        deals,
        viewMode,
        current_page,
        has_more,
        fetchListDeals,
        shouldFetchNextDeals,
        // Filter values destructured explicitly so we can memoize them
        // into a stable-identity object. Using `...filters` rest-spread
        // would build a fresh object every render, which breaks
        // useDebounce (Object.is sees a new ref each time → debounce
        // re-arms forever → infinite refetch loop after each completion).
        search, status, dealType, year, make, model, trim, users,
        monthlyMin, monthlyMax, msrpMin, msrpMax,
    } = useListDealsStore(
        useShallow((state) => ({
            deals: state.deals,
            viewMode: state.viewMode,
            fetchListDeals: state.fetchListDeals,
            shouldFetchNextDeals: state.shouldFetchNextDeals,
            current_page: state.current_page,
            has_more: state.has_more,

            search:     state.search,
            status:     state.status,
            dealType:   state.dealType,
            year:       state.year,
            make:       state.make,
            model:      state.model,
            trim:       state.trim,
            users:      state.users,
            monthlyMin: state.monthlyMin,
            monthlyMax: state.monthlyMax,
            msrpMin:    state.msrpMin,
            msrpMax:    state.msrpMax,
        }))
    )

    // pricing_id (from the "view desked quotes" link) isn't a UI filter — it's a
    // URL-only scope that narrows the list to quotes desked from that pricing row.
    const [searchParams] = useSearchParams()
    const pricingId = searchParams.get('pricing_id')

    // Stable-identity filters object: only allocates a new reference when
    // at least one filter VALUE actually changes. Without this memo,
    // useDebounce would never settle and the effect below would refetch
    // every 300ms forever.
    const filters = useMemo(() => ({
        search, status, dealType, year, make, model, trim, users,
        monthlyMin, monthlyMax, msrpMin, msrpMax,
    }), [
        search, status, dealType, year, make, model, trim, users,
        monthlyMin, monthlyMax, msrpMin, msrpMax,
    ])

    // 300 ms debounce so range-input typing doesn't fire a fetch per
    // keystroke. Dropdown selections also wait 300 ms — feels fine and
    // keeps the URL / fetch in lockstep.
    const debouncedFilters = useDebounce(filters, 300)

    // Map store filter values onto the query-string keys the backend
    // (eventually) consumes. Defaults are omitted so the URL / payload
    // stay short. The structured keys are stubs — the backend ignores
    // them today, which is fine; they'll start filtering as the API
    // grows support for each.
    const buildParams = (current_page, f) => {
        const queryParams = new URLSearchParams({})

        if (typeof current_page === 'number' && current_page !== 0) {
            queryParams.append("page", current_page + 1)
        }

        if (f?.search?.length) queryParams.append("s", f.search)

        const structured = {
            status:      f?.status,
            // Backend expects the deal type lowercased (e.g. "lease"). Leave
            // "All" untouched so the v !== 'All' skip below still applies.
            type:        f?.dealType && f.dealType !== 'All' ? f.dealType.toLowerCase() : f?.dealType,
            year:        f?.year,
            make:        f?.make,
            model:       f?.model,
            trim:        f?.trim,
            users:       f?.users,
            monthly_min: f?.monthlyMin,
            monthly_max: f?.monthlyMax,
            msrp_min:    f?.msrpMin,
            msrp_max:    f?.msrpMax,
        }
        for (const [k, v] of Object.entries(structured)) {
            if (v && v !== 'All') queryParams.append(k, v)
        }

        if (pricingId) queryParams.append("pricing_id", pricingId)

        return queryParams
    }


    // Page 1 belongs to `refetchOnFilterChange` below. CustomInfiniteScroll's
    // initialLoad probe runs in its componentDidMount — before this parent's
    // effect — so with no owner both fetched page 1 on every mount. The key
    // records which filter set the effect has claimed; the scroller may only
    // extend that set, never open it.
    const initialFetchKey = useRef(null)
    const currentFetchKey = buildParams(null, debouncedFilters).toString()

    useEffect(function refetchOnFilterChange(){
        initialFetchKey.current = currentFetchKey
        fetchListDeals(buildParams(null, debouncedFilters))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedFilters, pricingId])

    // Build the deal list as cards (grid) or table rows (list), inserting a
    // month divider/break whenever the updated_at month changes.
    const buildItems = (mode) => {
        const items = []
        let prevKey = null
        let prevName = null

        deals?.forEach((deal) => {
            const d = deal.updated_at ? new Date(deal.updated_at) : null
            if (d) {
                const key = monthKey(d)
                const name = monthName(d)
                if (prevKey !== null && key !== prevKey) {
                    items.push(
                        mode === 'list'
                            ? <MonthBreakRow key={`md-${prevKey}-${key}`} from={prevName} to={name}/>
                            : <MonthDivider key={`md-${prevKey}-${key}`} from={prevName} to={name}/>
                    )
                }
                prevKey = key
                prevName = name
            }

            const isSelected = Boolean(selectedDealId === deal.id)
            items.push(
                mode === 'list'
                    ? <ListDealRow deal={deal} key={deal.id} isSelected={isSelected}/>
                    : <ListDealCard deal={deal} key={deal.id} isSelected={isSelected}/>
            )
        })

        return items
    }

    return (
        <div className={classnames('ListDealsList', {'list-view': viewMode === 'list'})}>
            <CustomInfiniteScroll
                useWindow={true}
                // Auto-fill on mount: re-runs the offset check after every
                // state change, so if a freshly loaded page doesn't make the
                // document exceed the viewport it keeps fetching until it
                // does. Once the page overflows, real scroll events take over.
                initialLoad={true}
                pageStart={0}
                loadMore={(page) => {
                    // Page 1 for these filters is still the effect's to issue —
                    // don't race it into a duplicate.
                    if (initialFetchKey.current !== currentFetchKey) return

                    if (shouldFetchNextDeals()) {
                        fetchListDeals(buildParams(current_page, debouncedFilters))
                    }
                }}
                hasMore={has_more}
                className={'ListDealsList_container'}
            >
                {
                    viewMode === 'list'
                        ? (
                            <table className={'lv-table'}>
                                <thead>
                                    <tr>
                                        {LIST_COLUMNS.map((c) => <th key={c}>{c}</th>)}
                                        <th aria-label={'actions'}/>
                                    </tr>
                                </thead>
                                <tbody>
                                    {buildItems('list')}
                                </tbody>
                            </table>
                        )
                        : buildItems('grid')
                }
            </CustomInfiniteScroll>
        </div>
    )
}